import type { Editor } from '@tiptap/core';

const MARKDOWN_TABLE_DIVIDER_REGEX =
  /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/;

const normalizeMarkdownTableText = (input: string): string => {
  const trimmed = input.trim();

  if (trimmed.includes('\n') || !trimmed.includes('|---')) {
    return trimmed;
  }

  // Split single-line collapsed tables ("| a | b | |---| | 1 |") back onto rows.
  return trimmed.replace(/\|\s+\|/g, '|\n|');
};

const looksLikeMarkdownTable = (input: string): boolean => {
  const normalizedInput = normalizeMarkdownTableText(input);
  const lines = normalizedInput
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');

  if (lines.length < 2) {
    return false;
  }

  const hasPipeHeader = lines[0]?.includes('|') === true;
  const hasDivider = MARKDOWN_TABLE_DIVIDER_REGEX.test(lines[1] ?? '');

  if (!hasPipeHeader || !hasDivider) {
    return false;
  }

  return true;
};

/**
 * Convert a plain-text markdown paste into rich content (replaces the old
 * `tiptap-markdown` `transformPastedText`, which `@tiptap/markdown` dropped).
 *
 * Renders markdown to HTML rather than using `insertContent(..., { contentType:
 * 'markdown' })`: ProseMirror's HTML parser is schema-aware, so it fits block
 * nodes to the schema (e.g. lifts images out of paragraphs) instead of throwing
 * "Invalid content for node paragraph". Returns true if it handled the paste.
 *
 * Only the document editor uses this — the chat composer pastes plain text
 * verbatim (its schema can't render tables/most markdown, and a literal
 * markdown table reads fine for the LLM anyway).
 */
export function insertMarkdownFromPaste(
  editor: Editor,
  event: ClipboardEvent
): boolean {
  const clipboardData = event.clipboardData;
  if (clipboardData === null) {
    return false;
  }

  // Inside a code block, the paste must stay literal text within the block.
  // Rendering markdown to HTML here would produce paragraph nodes that the
  // schema can't nest in a code block, so ProseMirror lifts them out and drops
  // them in the row below. Defer to the default pipeline (plain-text inline).
  if (editor.isActive('codeBlock')) {
    return false;
  }

  // Defer to the default pipeline for rich pastes that already carry HTML.
  const html = clipboardData.getData('text/html');
  if (html.trim() !== '') {
    return false;
  }

  const plainText = clipboardData.getData('text/plain');
  if (plainText.trim() === '') {
    return false;
  }

  const markdown = looksLikeMarkdownTable(plainText)
    ? normalizeMarkdownTableText(plainText)
    : plainText;

  let renderedHtml: string;
  try {
    // `instance` is @tiptap/markdown's bundled `marked`; `.parse` is sync here.
    const rendered = editor.storage.markdown.manager.instance.parse(markdown);
    if (typeof rendered !== 'string' || rendered.trim() === '') {
      return false;
    }
    renderedHtml = rendered;
  } catch {
    return false;
  }

  event.preventDefault();
  editor.chain().focus().insertContent(renderedHtml).run();
  return true;
}
